import { useCallback, useEffect, useState } from 'react'
import { Button, Chip, Input } from '@heroui/react'
import { api } from '../lib/api'
import { screenTicked } from '../lib/permissions'
import { explain } from '../lib/staffErrors'
import type { OrgRole, Permission, Screen } from '../lib/types'
import { Icon } from '../ui/icons'
import { EmptyState } from '../ui/parts'
import { roleLabel } from '../lib/roleNames'
import { useStaffText } from '../ui/StaffText'
import { PermissionBoxes, screenLabel } from './permissionBoxes'
import { ChipList } from './peopleKit'
import { Confirm, useFlash, useSave } from './sectionKit'
import { IconTile, InnerPage, ListCard, ListRow, ListTable, NameCell, Notice, PageHead, RowArrow, toneOf, useOpenRecord } from './settingsKit'
import '../styles/admin.css'

type Kind = 'all' | 'builtIn' | 'custom'

/**
 * An organisation's roles, each a name and the screens it ticks. The locked
 * Administrator role holds everything and opens read-only; the rest may be
 * renamed, changed or deleted once nobody holds them.
 */

const MAX_NAME = 60

const blankDraft = (): Draft => ({ name: '', permissions: [] })

interface Draft {
  id?: string
  name: string
  roleKey?: string
  permissions: Permission[]
}

export function Roles({ organisation }: { organisation?: string }) {
  const { lang, say } = useStaffText()
  const [roleRows, setRoleRows] = useState<OrgRole[]>([])
  const [screens, setScreens] = useState<Screen[]>([])
  const [mayGive, setMayGive] = useState<Permission[]>([])
  // False until the first answer, so a slow line reads as loading, not as empty.
  const [loaded, setLoaded] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [find, setFind] = useState('')
  const [kind, setKind] = useState<Kind>('all')

  const load = useCallback(async () => {
    const [listed, catalogue] = await Promise.all([api.roles(organisation), api.permissionScreens(organisation)])
    setRoleRows(listed.roles)
    setScreens(catalogue.screens)
    setMayGive(catalogue.mayGive)
    setLoaded(true)
  }, [organisation])

  const { flash, toast } = useFlash()
  const { busy, error, setError, save } = useSave(load, flash)

  useEffect(() => {
    setLoaded(false)
    load().catch((err) => {
      setLoadError(explain(err, lang, 'common.unreachable'))
      setLoaded(true)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load])

  // The role open on its own page (#roles/<id>, #roles/new).
  const rec = useOpenRecord(roleRows, (r) => r.id, loaded)
  const open = (role?: OrgRole) => {
    setError('')
    rec.open(role)
  }
  const r = rec.row
  const editing = rec.isNew
    ? loaded && { draft: blankDraft(), locked: false, holders: 0 }
    : r && { draft: { id: r.id, name: r.name, roleKey: r.roleKey, permissions: r.permissions }, locked: r.locked, holders: r.holders }

  const access = (role: OrgRole) => screens.filter((s) => screenTicked(s, role.permissions)).map((s) => screenLabel(say, s.key))
  // A starting role keeps its key; the locked Administrator is one too.
  const builtIn = (role: OrgRole) => role.locked || Boolean(role.roleKey)
  const q = find.trim().toLocaleLowerCase()
  const shown = roleRows.filter(
    (r) =>
      (kind === 'all' || (kind === 'builtIn') === builtIn(r)) &&
      (!q || roleLabel(r.roleKey, r.name, lang).toLocaleLowerCase().includes(q)),
  )

  if (editing) {
    return (
      <>
        {toast}
        <RoleSheet
          key={rec.code}
          draft={editing.draft}
          locked={editing.locked}
          holders={editing.holders}
          screens={screens}
          mayGive={mayGive}
          busy={busy}
          error={error}
          onClose={rec.close}
          onSave={(draft) =>
            void save(
              () =>
                draft.id
                  ? api.updateRole(draft.id, { name: draft.name, permissions: draft.permissions }, organisation)
                  : api.createRole({ name: draft.name, permissions: draft.permissions }, organisation),
              say('common.saved'),
            ).then((ok) => ok && rec.close())
          }
          onDelete={(id) => void save(() => api.deleteRole(id, organisation), say('admin.roles.deleted')).then((ok) => ok && rec.close())}
        />
      </>
    )
  }

  return (
    <>
      <PageHead
        title={say('admin.section.roles')}
        line={say('settings.roles.line')}
        actions={
          <Button variant="primary" isDisabled={!loaded} onPress={() => open()}>
            <Icon name="plus" size={16} /> {say('admin.roles.new')}
          </Button>
        }
      />
      {toast}
      {loadError && <Notice className="os-gap">{loadError}</Notice>}
      {error && !editing && <Notice className="os-gap">{error}</Notice>}

      <ListCard
        title={say('admin.roles.title')}
        count={loaded ? say('settings.roles.count', { n: roleRows.length }) : undefined}
        search={find}
        onSearch={setFind}
        searchLabel={say('settings.roles.find')}
        chips={[
          { value: 'all', label: say('settings.roles.all') },
          { value: 'builtIn', label: say('settings.roles.builtIn'), count: roleRows.filter(builtIn).length },
          { value: 'custom', label: say('settings.roles.custom'), count: roleRows.filter((r) => !builtIn(r)).length },
        ]}
        chip={kind}
        onChip={setKind}
      >
        {!loaded && <p className="empty">{say('common.loading')}</p>}
        {loaded && roleRows.length === 0 && !loadError && (
          <EmptyState icon={<Icon name="lock" size={20} />} title={say('admin.roles.none')} />
        )}
        {loaded && roleRows.length > 0 && shown.length === 0 && <EmptyState title={say('admin.findNone', { q: find.trim() })} />}
        {shown.length > 0 && (
          <ListTable
            label={say('admin.roles.title')}
            cols="minmax(200px, 1.3fr) 96px 72px minmax(180px, 1.6fr) 16px"
            head={[say('admin.roles.col.role'), say('settings.roles.col.type'), say('admin.roles.col.people'), say('admin.roles.col.access'), '']}
          >
            {shown.map((role) => {
              const names = access(role)
              return (
                <ListRow key={role.id} onOpen={() => open(role)}>
                  <NameCell
                    tile={<IconTile icon={role.locked ? 'lock' : 'user'} tone={toneOf(role.id)} />}
                    name={roleLabel(role.roleKey, role.name, lang)}
                  />
                  <span>
                    <Chip size="sm" variant="soft">
                      {say(builtIn(role) ? 'settings.roles.builtIn' : 'settings.roles.custom')}
                    </Chip>
                  </span>
                  <span className="set-num">{role.holders}</span>
                  <span>
                    {role.locked ? (
                      <Chip size="sm" variant="secondary">
                        {say('admin.roles.everything')}
                      </Chip>
                    ) : (
                      <ChipList names={names} max={3} quiet={say('admin.roles.nothing')} />
                    )}
                  </span>
                  <RowArrow />
                </ListRow>
              )
            })}
          </ListTable>
        )}
      </ListCard>
    </>
  )
}

function RoleSheet({
  draft,
  locked,
  holders,
  screens,
  mayGive,
  busy,
  error,
  onClose,
  onSave,
  onDelete,
}: {
  draft: Draft
  locked: boolean
  holders: number
  screens: Screen[]
  mayGive: Permission[]
  busy: boolean
  error: string
  onClose: () => void
  onSave: (draft: Draft) => void
  onDelete: (id: string) => void
}) {
  const { lang, say } = useStaffText()
  const [d, setD] = useState(draft)
  const isNew = !draft.id
  const readOnly = locked
  const name = d.name.trim()
  const valid = !readOnly && name !== '' && name.length <= MAX_NAME

  return (
    <InnerPage
      back={say('admin.section.roles')}
      title={isNew ? say('admin.roles.new') : roleLabel(draft.roleKey, draft.name, lang)}
      onClose={onClose}
      onSubmit={readOnly ? undefined : () => onSave({ ...d, name })}
      submitLabel={isNew ? say('admin.roles.new') : say('common.save')}
      valid={valid}
      busy={busy}
      error={error || undefined}
      hideSubmit={readOnly}
      extra={
        readOnly ? (
          say('admin.roles.locked')
        ) : !isNew ? (
          holders > 0 ? (
            // Shown disabled with its reason, the count of holders.
            <span className="admin-inline">
              <Button variant="danger-soft" isDisabled>
                {say('admin.roles.delete')}
              </Button>
              <span className="muted">{say('admin.roles.holders', { n: holders })}</span>
            </span>
          ) : (
            <Confirm
              ask={say('admin.roles.delete')}
              question={say('admin.roles.deleteAsk')}
              yes={say('admin.roles.delete')}
              keep={say('common.cancel')}
              busy={busy}
              onYes={() => onDelete(draft.id!)}
            />
          )
        ) : undefined
      }
    >
      <label className="field">
        <span>{say('admin.roles.name')}</span>
        <Input fullWidth value={d.name} disabled={readOnly} maxLength={MAX_NAME} onChange={(e) => setD({ ...d, name: e.target.value })} />
      </label>

      <div className="field">
        <span>{say('people.permissions')}</span>
        <PermissionBoxes
          screens={screens}
          value={d.permissions}
          onChange={(permissions) => setD({ ...d, permissions })}
          disabled={readOnly}
          mayGive={mayGive}
        />
      </div>
    </InnerPage>
  )
}
